import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import Button from "@/design-system/button";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import Container from "@Components/Container";
import ImageLoader from "@Components/ImageLoader";
import {
    getAssetsImage,
    getCdnImage,
    isChrome,
    isEdgeChromium,
    isFirefox,
    sendToGA,
} from "@Components/Modules/Helpers";
import Text from "@Components/Text";
import { pluginInstallBtnId } from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import {
    type PropsWithStyleX,
    type QueryMdMax,
    type QuerySmMax,
    colors,
} from "../../../styles/tokens.stylex";
import { PluginTopComponentResources } from "./PluginTopComponent.Resources";
import { PluginTopComponentResourcesEn } from "./PluginTopComponent.Resources.En";

export type IPluginTopComponent = PropsWithStyleX & {
    refButtonRender?: React.MutableRefObject<Element>;
    pluginEdgeStore: string;
    pluginChromeStore: string;
    pluginFirefoxStore: string;
};

export default function PluginTopComponent(props: IPluginTopComponent) {
    const resources = languageSelector(PluginTopComponentResources, PluginTopComponentResourcesEn);
    const { isMobileOrTablet } = useViewportInfoContext();
    const [isSupportedBrowser, setIsSupportedBrowser] = useState<boolean>(false);

    useEffect(() => {
        const supported = isMobileOrTablet ? false : isChrome || isFirefox || isEdgeChromium;
        if (isSupportedBrowser !== supported) {
            setIsSupportedBrowser(supported);
        }
    }, []);

    const goToPluginStoreBtn = (
        <Button
            id={pluginInstallBtnId}
            as="button"
            size="M"
            variant={isSupportedBrowser ? "magentaRound" : "disabled"}
            disabled={!isSupportedBrowser}
            styleX={styles.btnGoToStore}
            onClick={() => {
                const link = isEdgeChromium
                    ? props.pluginEdgeStore
                    : isFirefox
                      ? props.pluginFirefoxStore
                      : isChrome
                        ? props.pluginChromeStore
                        : null;

                if (link === null) return;

                sendToGA("plugin_goToStore");
                window.open(link, "_blank", "noopener");
            }}
        >
            {isSupportedBrowser ? resources.btnActive : resources.btnInactive}
        </Button>
    );

    const generateBrowserIcon = (browserStore: string, iconPath: string, imgAlt: string, testId?: string) => (
        <Button
            as="a"
            styleX={styles.storeLink}
            size="None"
            variant="none"
            href={browserStore}
            target="_blank"
            data-testid={testId}
        >
            <img {...stylex.props(styles.storeLinkImg)} src={getAssetsImage(iconPath)} alt={imgAlt} />
        </Button>
    );

    return (
        <Container styleX={[styles.topComponent, props.styleX]}>
            <Text
                textElement="h1"
                fontStyle="h2"
                fontWeight="300"
                styleX={styles.title}
                dangerouslySetInnerHTML={{ __html: resources.title }}
            />
            <Text
                textElement="p"
                fontStyle="h4"
                styleX={styles.description}
                dangerouslySetInnerHTML={{ __html: resources.description }}
            />

            <ImageLoader
                styleX={styles.mainImage}
                size={800}
                src={getCdnImage(process.env.Plugin_MainImg)}
                alt="Main plugin sample"
            />
            {goToPluginStoreBtn}
            {isSupportedBrowser === false && (
                <Text fontStyle="primaryText" textElement="p" styleX={styles.errorInfo}>
                    {resources.browserAdditional}
                </Text>
            )}
            <div {...stylex.props(styles.browsers)}>
                <Text fontStyle="primaryText" textElement="p" styleX={styles.browserText}>
                    {resources.browserLabel}
                </Text>
                <div>
                    {generateBrowserIcon(
                        props.pluginChromeStore,
                        process.env.Logo_Chrome,
                        "google browser logo",
                        "plgn-chrome-store-icon",
                    )}
                    {generateBrowserIcon(props.pluginEdgeStore, process.env.Logo_Edge, "edge browser logo")}
                    {generateBrowserIcon(
                        props.pluginFirefoxStore,
                        process.env.Logo_Firefox,
                        "firefox browser logo",
                    )}
                </div>
            </div>
            {props?.refButtonRender?.current &&
                createPortal(goToPluginStoreBtn, props.refButtonRender.current)}
        </Container>
    );
}

const smMax: QuerySmMax = "@media (max-width: 989px)";
const mdMax: QueryMdMax = "@media (max-width: 1239px)";

const styles = stylex.create({
    topComponent: {
        alignItems: "center",
        display: "grid",
        gridTemplateColumns: { default: "1.2fr max-content", [smMax]: "1fr" },
        justifyItems: { default: null, [smMax]: "center" },
        rowGap: { default: "30px", [smMax]: "20px" },
        textAlign: { default: null, [smMax]: "center" },
    },

    title: {
        margin: 0,
        gridColumn: "1/2",
        gridRow: "1/2",
    },

    description: {
        gridColumn: "1/2",
        gridRow: "2/3",
        paddingRight: { default: "20%", [smMax]: 0 },
    },

    mainImage: {
        gridColumn: { default: "2/3", [smMax]: "1/2" },
        gridRow: { default: "1/6", [smMax]: "3/4" },
        justifySelf: "center",
        width: { default: "496px", [smMax]: "326px" },
    },

    btnGoToStore: {
        gridColumn: "1/2",
        gridRow: "4/5",
        fontSize: "1.125rem",
        justifySelf: { default: "start", [smMax]: "center" },
    },

    errorInfo: {
        gridRow: "5/6",
        color: colors.fg_Error,
        fontSize: ".875rem",
    },

    browsers: {
        gap: { default: "30px", [smMax]: "15px" },
        gridColumn: "1/2",
        gridRow: "3/4",
        alignItems: "center",
        display: { default: "flex", [smMax]: "none" },
        flexDirection: { default: null, [smMax]: "column" },
    },

    browserText: {
        flex: { default: null, [mdMax]: "1" },
    },

    storeLink: {
        padding: 0,
        marginBlock: "0",
        marginInline: "10px",
    },

    storeLinkImg: {
        height: "46px",
        width: "46px",
    },
});
